import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
const KEY = "cultlyf_cart";

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    try {
      const stored = localStorage.getItem(KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const lastSyncedRef = useRef("");
  const initialLoadRef = useRef(false);

  // Persist locally on every change
  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  // On login, hydrate from server (merge if local has items)
  useEffect(() => {
    if (!user) {
      initialLoadRef.current = false;
      return;
    }
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;
    (async () => {
      try {
        const res = await api.get("/cart");
        const serverItems = res.data?.items || [];
        if (serverItems.length === 0 && items.length > 0) {
          // Push local cart up
          await api.post("/cart/sync", { items });
        } else if (serverItems.length > 0 && items.length === 0) {
          setItems(serverItems);
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Sync to backend (debounced) whenever items change & user is logged in
  useEffect(() => {
    if (!user) return;
    const payload = JSON.stringify(items);
    if (payload === lastSyncedRef.current) return;
    const t = setTimeout(() => {
      api.post("/cart/sync", { items }).then(() => {
        lastSyncedRef.current = payload;
      }).catch(() => {});
    }, 700);
    return () => clearTimeout(t);
  }, [items, user]);

  const addItem = (item) => {
    setItems((cur) => {
      const idx = cur.findIndex(
        (i) => i.product_id === item.product_id && i.size === item.size && i.color === item.color
      );
      if (idx >= 0) {
        const copy = [...cur];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + item.qty };
        return copy;
      }
      return [...cur, item];
    });
  };

  const removeItem = (product_id, size, color) => {
    setItems((cur) => cur.filter((i) => !(i.product_id === product_id && i.size === size && i.color === color)));
  };

  const updateQty = (product_id, size, color, qty) => {
    setItems((cur) => cur.map((i) => (i.product_id === product_id && i.size === size && i.color === color) ? { ...i, qty } : i));
  };

  const clear = () => {
    setItems([]);
    if (user) {
      api.delete("/cart").catch(() => {});
    }
  };

  const total = items.reduce((s, i) => s + i.price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clear, total, count }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
